'use client'

import * as React from 'react'
import * as ReactDOM from 'react-dom'
import { focusRing } from '@repo/studio-design-system/utilities'
import { useIsMounted } from 'hooks/use-is-mounted'
import { Slot } from 'primitives/slot'
import { tw } from 'utils/classnames'
import { LoadingSpinner } from './loading-spinner'

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'ghost'
  | 'overlay'
  | 'danger'
  | 'promotional'
export type ButtonSize = 'xs' | 'sm' | 'md'

interface ButtonRootProps {
  variant?: ButtonVariant
  size?: ButtonSize
  asChild?: boolean
  loading?: boolean
}

type ButtonSharedProps = Omit<React.ComponentProps<'button'>, 'aria-label' | 'ref'> &
  ButtonRootProps & {
    ref?: React.Ref<HTMLButtonElement>
  }

export type ButtonProps<IconOnly extends boolean = false> = ButtonSharedProps &
  (IconOnly extends true
    ? {
        iconOnly: true
        'aria-label': string
      }
    : {
        iconOnly?: false
        'aria-label'?: string
      })

type IconOnlyButtonProps = ButtonProps<true>
type RegularButtonProps = ButtonProps

function ButtonRoot(props: IconOnlyButtonProps): React.ReactElement
function ButtonRoot(props: RegularButtonProps): React.ReactElement
function ButtonRoot({
  size = 'md',
  variant = 'primary',
  asChild,
  loading,
  iconOnly,
  ref,
  ...props
}: IconOnlyButtonProps | RegularButtonProps) {
  const className = tw(
    'rounded-2 outline-none',
    focusRing,
    'relative isolate',
    'flex items-center justify-center',
    props.disabled && 'cursor-not-allowed',
    iconOnly ? buttonClasses.iconOnlySizes[size] : buttonClasses.sizes[size],
    buttonClasses.variants[variant],
    '[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg]:transition-transform [&_svg]:duration-300 [&_svg]:ease-in-out',
    props.className,
  )

  const Comp = asChild ? Slot : 'button'

  const innerRef = React.useRef<HTMLButtonElement>(null)
  React.useImperativeHandle(ref, () => innerRef.current!)

  const hasSetLoading = React.useRef(false)
  if (loading) {
    hasSetLoading.current = true
  }

  return (
    <>
      <Comp {...props} className={tw(className)} ref={innerRef} />
      {hasSetLoading.current ? (
        <ButtonLoader containerRef={innerRef} loading={loading} size={size} />
      ) : null}
    </>
  )
}

function ButtonLoader({
  containerRef,
  loading,
  size,
}: {
  containerRef?: React.RefObject<HTMLButtonElement | null>
  loading?: boolean
  size: ButtonSize
}) {
  const isMounted = useIsMounted()

  if (!isMounted || !containerRef?.current) {
    return null
  }

  return ReactDOM.createPortal(
    <LoadingSpinner loading={loading} size={size} />,
    containerRef.current,
  )
}

export const Button = ButtonRoot

const buttonClasses = {
  variants: {
    primary: [
      'bg-theme_semantic_action_primary_background-color-default text-theme_semantic_action_primary_foreground-color-default',
      'hover:bg-theme_semantic_action_primary_background-color-hover hover:text-theme_semantic_action_primary_foreground-color-hover',
      'active:bg-theme_semantic_action_primary_background-color-active active:text-theme_semantic_action_primary_foreground-color-active',
      'disabled:bg-theme_semantic_action_primary_background-color-disabled disabled:text-theme_semantic_action_primary_foreground-color-disabled',
      'focus-visible:bg-theme_semantic_action_primary_background-color-focus focus-visible:text-theme_semantic_action_primary_foreground-color-focus',
    ].join(' '),
    secondary: [
      'bg-theme_semantic_action_secondary_background-color-default text-theme_semantic_action_secondary_foreground-color-default',
      'hover:bg-theme_semantic_action_secondary_background-color-hover hover:text-theme_semantic_action_secondary_foreground-color-hover',
      'active:bg-theme_semantic_action_secondary_background-color-active active:text-theme_semantic_action_secondary_foreground-color-active',
      'disabled:bg-theme_semantic_action_secondary_background-color-disabled disabled:text-theme_semantic_action_secondary_foreground-color-disabled',
      'focus-visible:bg-theme_semantic_action_secondary_background-color-focus focus-visible:text-theme_semantic_action_secondary_foreground-color-focus',
    ].join(' '),
    tertiary: [
      'bg-transparent border border-theme_semantic_action_tertiary_border-color-default text-theme_semantic_action_tertiary_foreground-color-default',
      'hover:bg-theme_semantic_action_tertiary_background-color-hover hover:text-theme_semantic_action_tertiary_foreground-color-hover',
      'active:bg-theme_semantic_action_tertiary_background-color-active active:text-theme_semantic_action_tertiary_foreground-color-active',
      'disabled:border-theme_semantic_action_tertiary_border-color-disabled disabled:text-theme_semantic_action_tertiary_foreground-color-disabled',
      'focus-visible:bg-theme_semantic_action_tertiary_background-color-focus focus-visible:text-theme_semantic_action_tertiary_foreground-color-focus',
    ].join(' '),
    ghost: [
      'bg-transparent text-theme_semantic_action_ghost_foreground-color-default',
      'hover:bg-theme_semantic_action_ghost_background-color-hover hover:text-theme_semantic_action_ghost_foreground-color-hover',
      'active:bg-theme_semantic_action_ghost_background-color-active active:text-theme_semantic_action_ghost_foreground-color-active',
      'disabled:bg-theme_semantic_action_ghost_background-color-disabled disabled:text-theme_semantic_action_ghost_foreground-color-disabled',
      'focus-visible:bg-theme_semantic_action_ghost_background-color-focus focus-visible:text-theme_semantic_action_ghost_foreground-color-focus',
    ].join(' '),
    overlay: [
      'bg-theme_semantic_action_inverse_background-color-default text-theme_semantic_action_inverse_foreground-color-default',
      'hover:bg-theme_semantic_action_inverse_background-color-hover hover:text-theme_semantic_action_inverse_foreground-color-hover',
      'active:bg-theme_semantic_action_inverse_background-color-active active:text-theme_semantic_action_inverse_foreground-color-active',
      'disabled:bg-theme_semantic_action_inverse_background-color-disabled disabled:text-theme_semantic_action_inverse_foreground-color-disabled',
      'focus-visible:bg-theme_semantic_action_inverse_background-color-focus focus-visible:text-theme_semantic_action_inverse_foreground-color-focus',
    ].join(' '),
    danger: [
      'bg-theme_semantic_action_danger_background-color-default text-theme_semantic_action_danger_foreground-color-default',
      'hover:bg-theme_semantic_action_danger_background-color-hover hover:text-theme_semantic_action_danger_foreground-color-hover',
      'active:bg-theme_semantic_action_danger_background-color-active active:text-theme_semantic_action_danger_foreground-color-active',
      'disabled:bg-theme_semantic_action_danger_background-color-disabled disabled:text-theme_semantic_action_danger_foreground-color-disabled',
      'focus-visible:bg-theme_semantic_action_danger_background-color-focus focus-visible:text-theme_semantic_action_danger_foreground-color-focus',
    ].join(' '),
    promotional: [
      'bg-theme_semantic_action_promotional_background-color-default text-theme_semantic_action_promotional_foreground-color-default',
      'hover:bg-theme_semantic_action_promotional_background-color-hover hover:text-theme_semantic_action_promotional_foreground-color-hover',
      'active:bg-theme_semantic_action_promotional_background-color-active active:text-theme_semantic_action_promotional_foreground-color-active',
      'disabled:bg-theme_semantic_action_promotional_background-color-disabled disabled:text-theme_semantic_action_promotional_foreground-color-disabled',
      'focus-visible:bg-theme_semantic_action_promotional_background-color-focus focus-visible:text-theme_semantic_action_promotional_foreground-color-focus',
    ].join(' '),
  },
  sizes: {
    md: ['h-10 px-4 gap-3 [&_svg]:size-6 [&_svg]:-mx-1 typography-14'].join(' '),
    sm: ['h-8 px-3 gap-2 [&_svg]:size-5 [&_svg]:-mx-1 typography-14'].join(' '),
    xs: ['h-6 px-2 gap-2 [&_svg]:size-4 [&_svg]:-mx-1 typography-12'].join(' '),
  },
  iconOnlySizes: {
    md: ['box-border flex h-10 w-10 items-center justify-center px-2 [&>svg]:size-6'].join(' '),
    sm: ['box-border flex h-8 w-8 items-center justify-center px-1 [&>svg]:size-5'].join(' '),
    xs: ['box-border flex h-6 w-6 items-center justify-center px-1 [&>svg]:size-4'].join(' '),
  },
}

export type IconButtonProps = Omit<React.ComponentProps<'button'>, 'ref'> & {
  size?: ButtonSize
  variant?: ButtonVariant
  asChild?: boolean
  loading?: boolean
  ref?: React.Ref<HTMLButtonElement>
}

/** @deprecated Use `<Button iconOnly>` instead. */
export const IconButton = function ({
  ref,
  size = 'md',
  variant = 'primary',
  asChild,
  loading,
  ...props
}: IconButtonProps) {
  const className = tw(
    'relative isolate',
    'rounded-2 outline-none',
    focusRing,
    props.disabled && 'cursor-not-allowed',
    buttonClasses.iconOnlySizes[size],
    buttonClasses.variants[variant],
    '[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg]:transition-transform [&_svg]:duration-300 [&_svg]:ease-in-out',
    props.className,
  )

  const Comp = asChild ? Slot : 'button'

  const innerRef = React.useRef<HTMLButtonElement>(null)
  React.useImperativeHandle(ref, () => innerRef.current!)

  const hasSetLoading = React.useRef(false)
  if (loading) {
    hasSetLoading.current = true
  }

  return (
    <>
      <Comp {...props} className={className} ref={innerRef} />
      {hasSetLoading.current ? (
        <ButtonLoader containerRef={innerRef} loading={loading} size={size} />
      ) : null}
    </>
  )
}
IconButton.displayName = 'IconButton'
